import React, { Fragment, useState } from "react";
import { string, func, bool, object, arrayOf, shape, number } from "prop-types";
import { connect } from "react-redux";
import { compose, getComponentDisplayName } from "../helpers";

import { updateUserInProfileSilent, storeWorkHistory } from "../redux/actions";

import {
  workHistoryToUiModel,
  uiModelToWorkHistory,
  addIdToWorkHistory,
  workHistoryDateDescend,
  loadWorkHistoryUi,
} from "../helpers/workHistory";

import history from "../history";

import { Loading, Backdrop, Button } from "../common";

import {
  WorkHistoryForm,
  WorkHistoryCard,
} from "../components/BasicInfo/Edit/WorkHistory";
import Popup from "../common/Popup/Popup";

const withWorkHistoryState = (Component) => {
  const wrappedComponent = (props) => {
    const initialState = {
      editingId: null,
      title: "",
      companyName: "",
      fromMonth: "",
      toMonth: "",
      fromYear: "",
      toYear: "",
      description: "",
      currentlyWorkHere: false,
      isEditing: false,
    };
    const [state, setState] = useState(initialState);
    const stateFunctions = {
      onChange: (key, val) => {
        if (key === "currentlyWorkHere" && val) {
          setState({
            ...state,
            [key]: val,
            toMonth: null,
            toYear: null,
          });
        } else {
          setState({ ...state, [key]: val });
        }
      },
      onEditClick: (workHistoryId) => {
        setState({
          ...state,
          ...loadWorkHistoryUi(props.workHistories, workHistoryId),
          isEditing: true,
          editingId: workHistoryId,
        });
      },
      clearForm: () => setState(initialState),
    };
    return <Component {...props} {...state} {...stateFunctions} />;
  };
  wrappedComponent.displayName = `withWorkHistoryState(${getComponentDisplayName(
    Component
  )})`;
  wrappedComponent.propTypes = {
    workHistories: arrayOf(WorkHistoryCard.propTypes.workHistory).isRequired,
  };
  return wrappedComponent;
};

const withWorkHistoryProps = (Component) => {
  const wrappedComponent = (props) => {
    const {
      appData: { candidateRef, emailAddress },
      workHistories,
      editingId,
      saveWorkHistory,
      clearForm,
      updateUser,
      ...workHistoryUiItem
    } = props;

    const updateWorkHistories = (updatedHistories) => {
      const updatedAppData = {
        candidateRef,
        emailAddress,
        workHistory: updatedHistories,
      };
      return updateUser(candidateRef, updatedAppData);
    };
    const workHistoryFunctions = {
      addWorkHistory: () => {
        const updatedWorkHistories = [
          uiModelToWorkHistory(workHistoryUiItem),
          ...workHistories,
        ];
        updateWorkHistories(updatedWorkHistories, "cv");
        clearForm();
      },
      editWorkHistory: () => {
        const updatedWorkHistories = workHistories.map((workHistory) =>
          workHistory.id === workHistoryUiItem.id
            ? uiModelToWorkHistory({ ...workHistoryUiItem, id: editingId })
            : workHistory
        );
        updateWorkHistories(updatedWorkHistories);
        clearForm();
      },
      removeWorkHistory: (removedId) => {
        const updatedAppData = workHistories.filter(
          ({ id }) => id !== removedId
        );
        updateWorkHistories(updatedAppData);
      },
    };
    return <Component {...props} {...workHistoryFunctions} />;
  };
  wrappedComponent.propTypes = {
    appData: shape({
      candidateRef: number.isRequired,
      emailAddress: string.isRequired,
    }),
    workHistories: arrayOf(WorkHistoryCard.propTypes.workHistory).isRequired,
    editingId: string,
    saveWorkHistory: func.isRequired,
    clearForm: func.isRequired,
    updateUser: func.isRequired,
  };
  wrappedComponent.displayName = `withWorkHistoryProps(${getComponentDisplayName(
    Component
  )})`;
  return wrappedComponent;
};

const WorkHistory = (props) => {
  const {
    appData,
    workHistories,
    onEditClick,
    addWorkHistory,
    editWorkHistory,
    removeWorkHistory,
    isEditing,
    clearForm,
  } = props;

  const [isFormOpen, setFormOpen] = useState(false);
  const closePopup = () => {
    setFormOpen(false);
    clearForm();
  };

  return (
    <Fragment>
      <Backdrop show={!appData}>
        <Loading />
      </Backdrop>
      <div style={{ minHeight: "60vh" }} className="p-2 p-sm-0">
        <div className="row justify-content-center">
          <h2 className="col-12 col-sm-8 col-lg-7 px-0 mb-1 mt-2 text-spacedOut text-bluishGray">
            Your work history
          </h2>
          <div className="col-12 col-sm-8 col-lg-7 p-1 mb-2 section-body">
            <p>
              Tell us about your work history. <br /> Please add as much
              information as you can, as this will help with your job search.
            </p>
            <div className="d-flex justify-content-end mt-2 mt-sm-0 flex-sm-row flex-column-reverse">
              <Button
                styles={{ minWidth: "175px", margin: "5px" }}
                onClick={() => {
                  history.push("/profile");
                }}
                classes="btn btn-outline-dark"
              >
                Back to My Account
              </Button>
              <Button
                styles={{ margin: "5px" }}
                onClick={() => setFormOpen(true)}
                classes="btn btn-primary"
              >
                Add
              </Button>
            </div>
          </div>
          <Popup isOpen={isFormOpen} handleCloseClick={() => {}} overNav>
            <WorkHistoryForm
              {...props}
              isEditing={isEditing}
              triggerSave={isEditing ? editWorkHistory : addWorkHistory}
              closePopup={() => closePopup()}
            />
          </Popup>
          {workHistories
            .sort(workHistoryDateDescend)
            .map(workHistoryToUiModel)
            .map((workHistory) => (
              <WorkHistoryCard
                key={workHistory.id}
                workHistory={workHistory}
                onEdit={(e) => {
                  onEditClick(e);
                  setFormOpen(true);
                }}
                onRemove={removeWorkHistory}
              />
            ))}
        </div>
      </div>
    </Fragment>
  );
};

WorkHistory.propTypes = {
  appData: object.isRequired,
  workHistories: arrayOf(WorkHistoryCard.propTypes.workHistory).isRequired,
  routeWhenDone: string.isRequired,
  loading: bool.isRequired,
  onEditClick: func.isRequired,
  addWorkHistory: func.isRequired,
  editWorkHistory: func.isRequired,
  removeWorkHistory: func.isRequired,
  isEditing: bool.isRequired,
  clearForm: func,
};

const mapStateToProps = ({ core }) => ({
  appData: core.app,
  workHistories: core.app.workHistory
    ? core.app.workHistory.map(addIdToWorkHistory)
    : [],
  loading: core.loading,
});

const mapDispatchToProps = {
  updateUser: updateUserInProfileSilent,
  saveWorkHistory: storeWorkHistory,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

const withApi = compose(withRedux, withWorkHistoryState, withWorkHistoryProps);

export default withApi(WorkHistory);
